/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2021 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

const getCellText = (node: Node): string => {
  if (node.nodeType === 3) {
    return node.textContent ?? ''
  }

  if (node.nodeName === 'BR') {
    return '\n'
  }

  return Array.from(node.childNodes).map(getCellText).join('')
}

const parseHtmlTable = (html: string) => {
  if (!html) {
    return null
  }

  const document = new DOMParser().parseFromString(html, 'text/html')
  const table = document.querySelector('table')

  if (!table?.rows.length) {
    return null
  }

  return Array.from(table.rows, row =>
    Array.from(row.cells, cell => getCellText(cell)),
  )
}

const parseTabSeparatedText = (text: string) => {
  const rows: string[][] = []
  const normalizedText = text.replace(/\r\n?/g, '\n').replace(/\n$/, '')
  let row: string[] = []
  let cell = ''
  let quoted = false
  let quotedCellHasSeparator = false
  let startOfCell = true

  for (let index = 0; index < normalizedText.length; index++) {
    const character = normalizedText[index]

    if (character === '"' && quoted && normalizedText[index + 1] === '"') {
      cell += '"'
      quotedCellHasSeparator = true
      index++
    } else if (character === '"' && quoted) {
      if (
        !quotedCellHasSeparator ||
        (normalizedText[index + 1] &&
          normalizedText[index + 1] !== '\t' &&
          normalizedText[index + 1] !== '\n')
      ) {
        cell = `"${cell}"`
      }
      quoted = false
    } else if (character === '"' && startOfCell) {
      quoted = true
      quotedCellHasSeparator = false
      startOfCell = false
    } else if (character === '\t' && !quoted) {
      row.push(cell)
      cell = ''
      startOfCell = true
    } else if (character === '\n' && !quoted) {
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
      startOfCell = true
    } else {
      cell += character
      if (quoted && (character === '\t' || character === '\n')) {
        quotedCellHasSeparator = true
      }
      startOfCell = false
    }
  }

  if (quoted) {
    cell = `"${cell}`
  }

  row.push(cell)
  rows.push(row)

  return rows
}

const joinNumberedRows = (rows: string[][], expectedColumns: number) => {
  if (rows.every(row => row.length === expectedColumns)) {
    return rows
  }

  // Текст из PDF может переносить содержимое ячейки на новую строку.
  // Объединяем его только при последовательной нумерации и совпадении ширины.
  const joinedRows: string[][] = []
  let currentRow: string[] | null = null
  let nextNumber: number | null = null

  for (const row of rows) {
    const numberMatch = /^(\d+)[,.]?$/.exec(row[0])

    if (numberMatch && row.length > 1) {
      const number = Number(numberMatch[1])

      if (nextNumber !== null && number !== nextNumber) {
        return rows
      }

      currentRow = [...row]
      joinedRows.push(currentRow)
      nextNumber = number + 1
    } else if (currentRow && row.length > 0) {
      currentRow[currentRow.length - 1] += `\n${row[0]}`
      currentRow.push(...row.slice(1))
    } else {
      return rows
    }
  }

  return joinedRows.length > 1 &&
    joinedRows.every(row => row.length === expectedColumns)
    ? joinedRows
    : rows
}

export const parseClipboardTable = (
  html: string,
  text: string,
  expectedColumns?: number,
) => {
  const htmlRows = parseHtmlTable(html)

  if (htmlRows) {
    return htmlRows
  }

  const textRows = parseTabSeparatedText(text)

  return expectedColumns
    ? joinNumberedRows(textRows, expectedColumns)
    : textRows
}
